import React, { useContext, useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import Navbar from "../../components/Navbar";
import API from "../../api/api";
import { AuthContext } from "../../context/AuthContext";
import SearchableSelect from "../../components/common/SearchableSelect";
import { errorAlert, successAlert, warningAlert } from "../../utils/alert";

const getPageNumbers = (currentPage, totalPages) => {
  const pages = [];
  const maxButtons = 5;
  if (totalPages <= maxButtons) {
    for (let i = 1; i <= totalPages; i++) {
      pages.push(i);
    }
  } else {
    pages.push(1);
    let start = Math.max(2, currentPage - 1);
    let end = Math.min(totalPages - 1, currentPage + 1);
    if (currentPage <= 2) {
      end = 3;
    } else if (currentPage >= totalPages - 1) {
      start = totalPages - 2;
    }
    if (start > 2) {
      pages.push("...");
    }
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    if (end < totalPages - 1) {
      pages.push("...");
    }
    pages.push(totalPages);
  }
  return pages;
};

const formatDate = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
};

const batchRequests = async (items, fn, limit = 5) => {
  const results = [];
  for (let i = 0; i < items.length; i += limit) {
    const chunk = items.slice(i, i + limit);
    results.push(...(await Promise.all(chunk.map(fn))));
  }
  return results;
};

const VolunteerRegistrations = () => {
  const { role } = useContext(AuthContext);
  const [registrations, setRegistrations] = useState([]);
  const [stats, setStats] = useState([]);
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState("");
  const [eventLevelFilter, setEventLevelFilter] = useState("all");
  const [eventYearFilter, setEventYearFilter] = useState("all");
  const [districtOptions, setDistrictOptions] = useState([]);
  const [selectedDistrictFilter, setSelectedDistrictFilter] = useState("all");

  useEffect(() => {
    if (eventLevelFilter !== "district") {
      setSelectedDistrictFilter("all");
    }
  }, [eventLevelFilter]);

  const summaryCounts = useMemo(() => {
    const counts = { Total: 0, Pending: 0, Approved: 0, Rejected: 0 };
    (Array.isArray(registrations) ? registrations : []).forEach((r) => {
      counts.Total++;
      const s = String(r.status || "Pending");
      if (s === "Approved") counts.Approved++;
      else if (s === "Rejected") counts.Rejected++;
      else counts.Pending++;
    });
    return counts;
  }, [registrations]);

  const [statusFilter, setStatusFilter] = useState("All");
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const isAllowed = ["admin", "secretary", "district"].includes((role || "").toLowerCase());

  const loadData = async () => {
    setLoading(true);
    setSelectedIds([]);
    try {
      const [regsRes, statsRes, eventsRes] = await Promise.all([
        API.fetchVolunteerRegistrations(),
        API.fetchVolunteerRegistrationStats(),
        API.fetchAdminEvents(),
      ]);

      setRegistrations(regsRes?.data || regsRes || []);
      setStats(statsRes?.data || statsRes || []);
      setEvents(eventsRes?.data || eventsRes || []);

      const roleLower = String(role || "").toLowerCase();
      if (roleLower !== "district") {
        try {
          const dists = await API.fetchDistrictOptions();
          setDistrictOptions(dists || []);
        } catch (e) {
          console.error(e);
        }
      }
    } catch (err) {
      errorAlert("Load failed", err?.message || "Failed to load volunteer registrations.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isAllowed) return;
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAllowed]);

  const filteredStats = useMemo(() => {
    return (stats || []).filter((s) => {
      const ev = (events || []).find((e) => String(e.id) === String(s.event_id));
      if (!ev) return false;
      if (eventLevelFilter !== "all") {
        const evLevel = String(ev.event_level || "").toLowerCase();
        if (eventLevelFilter === "state") {
          if (evLevel === "district") return false;
        } else if (eventLevelFilter === "district") {
          if (evLevel !== "district") return false;
          if (selectedDistrictFilter !== "all" && String(ev.district_id) !== String(selectedDistrictFilter)) return false;
        }
      }
      if (eventYearFilter !== "all") {
        const evYear = ev?.event_date ? new Date(ev.event_date).getFullYear() : (ev?.date ? new Date(ev.date).getFullYear() : null);
        if (String(evYear) !== String(eventYearFilter)) return false;
      }

      if (statusFilter !== "All") {
        const val = String(statusFilter).toLowerCase();
        if (val === "pending" && (s.pending || 0) === 0) return false;
        if (val === "approved" && (s.approved || 0) === 0) return false;
        if (val === "rejected" && (s.rejected || 0) === 0) return false;
      }

      return true;
    });
  }, [stats, events, eventLevelFilter, eventYearFilter, selectedDistrictFilter, statusFilter]);

  const filteredEventsForDropdown = useMemo(() => {
    return (events || []).filter((ev) => {
      const evLevel = String(ev.event_level || "").toLowerCase();
      if (eventLevelFilter === "state") {
        if (evLevel === "district") return false;
      } else if (eventLevelFilter === "district") {
        if (evLevel !== "district") return false;
        if (selectedDistrictFilter !== "all" && String(ev.district_id) !== String(selectedDistrictFilter)) return false;
      }
      if (eventYearFilter !== "all") {
        const evYear = ev?.event_date ? new Date(ev.event_date).getFullYear() : (ev?.date ? new Date(ev.date).getFullYear() : null);
        if (String(evYear) !== String(eventYearFilter)) return false;
      }

      // Filter by status if selected
      const s = (stats || []).find((st) => String(st.event_id) === String(ev.id));
      if (statusFilter !== "All" && s) {
        const val = String(statusFilter).toLowerCase();
        if (val === "pending" && (s.pending || 0) === 0) return false;
        if (val === "approved" && (s.approved || 0) === 0) return false;
        if (val === "rejected" && (s.rejected || 0) === 0) return false;
      }

      return true;
    });
  }, [events, eventLevelFilter, eventYearFilter, selectedDistrictFilter, statusFilter, stats]);

  const filteredRows = useMemo(() => {
    const rows = Array.isArray(registrations) ? registrations : [];
    return rows.filter((row) => {
      const ev = (events || []).find((e) => String(e.id) === String(row.event_id));
      if (selectedEvent && String(row.event_id) !== String(selectedEvent)) return false;
      
      if (!selectedEvent && eventLevelFilter !== "all" && ev) {
        const evLevel = String(ev.event_level || "").toLowerCase();
        if (eventLevelFilter === "state") {
          if (evLevel === "district") return false;
        } else if (eventLevelFilter === "district") {
          if (evLevel !== "district") return false;
          if (selectedDistrictFilter !== "all" && String(ev.district_id) !== String(selectedDistrictFilter)) return false;
        }
      }
      if (!selectedEvent && eventYearFilter !== "all" && ev) {
        const evYear = ev?.event_date ? new Date(ev.event_date).getFullYear() : (ev?.date ? new Date(ev.date).getFullYear() : null);
        if (String(evYear) !== String(eventYearFilter)) return false;
      }

      if (statusFilter !== "All" && String(row.status) !== statusFilter) return false;
      return true;
    });
  }, [registrations, selectedEvent, statusFilter, events, eventLevelFilter, eventYearFilter, selectedDistrictFilter]);

  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const sortedRows = useMemo(() => {
    if (!sortConfig.key) return filteredRows;
    return [...filteredRows].sort((a, b) => {
      let aVal = a[sortConfig.key] ?? "";
      let bVal = b[sortConfig.key] ?? "";
      const comparison = String(aVal).localeCompare(String(bVal), undefined, { numeric: true });
      return sortConfig.direction === "asc" ? comparison : -comparison;
    });
  }, [filteredRows, sortConfig]);

  // Reset pagination when search filter or limit changes
  useEffect(() => {
    setPage(1);
  }, [statusFilter, selectedEvent, eventLevelFilter, eventYearFilter, selectedDistrictFilter, limit]);

  const paginatedRows = useMemo(() => {
    const start = (page - 1) * limit;
    return sortedRows.slice(start, start + limit);
  }, [sortedRows, page, limit]);

  const totalPages = Math.ceil(filteredRows.length / limit) || 1;

  const isAllSelected = useMemo(() => {
    if (filteredRows.length === 0) return false;
    return filteredRows.every((row) => selectedIds.includes(row.id));
  }, [filteredRows, selectedIds]);

  const toggleSelectAll = () => {
    if (isAllSelected) {
      const filteredRowIds = filteredRows.map((r) => r.id);
      setSelectedIds((prev) => prev.filter((id) => !filteredRowIds.includes(id)));
    } else {
      const filteredRowIds = filteredRows.map((r) => r.id);
      setSelectedIds((prev) => Array.from(new Set([...prev, ...filteredRowIds])));
    }
  };

  const toggleSelectRow = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleBulkToggleStatus = async () => {
    if (!selectedIds.length) {
      warningAlert("No Selection", "Please select at least one volunteer first.");
      return;
    }

    const confirm = await Swal.fire({
      title: "Toggle Status?",
      text: `Toggle status for ${selectedIds.length} volunteer(s) based on their current status (Approved <-> Rejected)?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Toggle All",
    });
    if (!confirm.isConfirmed) return;

    Swal.fire({
      title: "Updating...",
      text: `Processing ${selectedIds.length} volunteer(s)...`,
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading(),
    });

    setLoading(true);
    try {
      let completed = 0;
      await batchRequests(selectedIds, async (id) => {
        const row = registrations.find((r) => r.id === id);
        const currentStatus = row?.status || "Pending";
        const nextStatus = currentStatus === "Approved" ? "Rejected" : "Approved";
        const note = nextStatus === "Rejected" ? "Bulk status toggled to Rejected" : "";
        const res = await API.updateVolunteerRegistrationStatus(id, nextStatus, note);
        completed++;
        Swal.update({
          text: `Updated ${completed} of ${selectedIds.length} volunteers...`
        });
        return res;
      });
      Swal.close();
      successAlert("Success", "Selected volunteers' status toggled successfully.");
      setSelectedIds([]);
      await loadData();
    } catch (err) {
      Swal.close();
      errorAlert("Bulk toggle failed", err?.message || "Failed to update status.");
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleStatusChange = async (row, nextStatus) => {
    if (!nextStatus || nextStatus === row.status) return;

    let note = "";
    if (nextStatus === "Rejected") {
      const reasonRes = await Swal.fire({
        title: "Reject Volunteer?",
        text: "You can add optional note.",
        input: "text",
        inputPlaceholder: "Reason (optional)",
        showCancelButton: true,
        confirmButtonText: "Reject",
      });
      if (!reasonRes.isConfirmed) return;
      note = String(reasonRes.value || "").trim();
    } else {
      const confirm = await Swal.fire({
        title: `${nextStatus} Volunteer?`,
        text: `Status will change to ${nextStatus}.`,
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Confirm",
      });
      if (!confirm.isConfirmed) return;
    }

    try {
      await API.updateVolunteerRegistrationStatus(row.id, nextStatus, note);
      successAlert("Status updated", `Volunteer registration ${nextStatus.toLowerCase()} successfully.`);
      await loadData();
    } catch (err) {
      errorAlert("Update failed", err?.message || "Failed to update status.");
    }
  };

  const downloadCertificate = async (row, regenerate = false) => {
    try {
      await API.downloadVolunteerCertificate(row.id, { regenerate });
      successAlert(
        "Certificate ready",
        regenerate ? "Certificate regenerated and downloaded." : "Certificate downloaded."
      );
      await loadData();
    } catch (err) {
      errorAlert("Download failed", err?.message || "Failed to download certificate.");
    }
  };

  if (!isAllowed) {
    return (
      <div className="flex justify-center items-center h-screen text-red-500">
        Unauthorized Access
      </div>
    );
  }

  return (
    <>
      <Navbar />
      <div className="max-w-7xl mx-auto p-3 sm:p-4 md:p-6 mobile-page-pad">
        <section className="mb-6 rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 via-white to-cyan-50 p-4 sm:p-5 shadow-sm">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-cyan-700">Volunteer Desk</p>
              <h1 className="text-2xl sm:text-3xl font-semibold text-slate-900 mt-1">Volunteer / Escort Registrations</h1>
              <p className="text-sm text-slate-600 mt-1">
                Review volunteer entries, approve status and issue certificates from a single queue.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
              <button
                type="button"
                onClick={() => setStatusFilter("All")}
                className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-left cursor-pointer hover:bg-slate-50 transition-all"
              >
                <p className="text-[11px] uppercase tracking-wide text-slate-500">Total</p>
                <p className="text-lg font-semibold text-slate-900">{summaryCounts.Total}</p>
              </button>
              <button
                type="button"
                onClick={() => setStatusFilter("All")}
                className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-left cursor-pointer hover:bg-slate-50 transition-all"
              >
                <p className="text-[11px] uppercase tracking-wide text-slate-500">Filtered</p>
                <p className="text-lg font-semibold text-slate-900">{filteredRows.length}</p>
              </button>
              <button
                type="button"
                onClick={() => setStatusFilter("Pending")}
                className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-left cursor-pointer hover:bg-amber-100/50 transition-all"
              >
                <p className="text-[11px] uppercase tracking-wide text-amber-700">Pending</p>
                <p className="text-lg font-semibold text-amber-800">{summaryCounts.Pending}</p>
              </button>
              <button
                type="button"
                onClick={() => setStatusFilter("Approved")}
                className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-left cursor-pointer hover:bg-emerald-100/50 transition-all"
              >
                <p className="text-[11px] uppercase tracking-wide text-emerald-700">Approved</p>
                <p className="text-lg font-semibold text-emerald-800">{summaryCounts.Approved}</p>
              </button>
              <button
                type="button"
                onClick={() => setStatusFilter("Rejected")}
                className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-left cursor-pointer hover:bg-rose-100/50 transition-all"
              >
                <p className="text-[11px] uppercase tracking-wide text-rose-700">Rejected</p>
                <p className="text-lg font-semibold text-rose-800">{summaryCounts.Rejected}</p>
              </button>
            </div>
          </div>
        </section>
        <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow mb-5 flex flex-wrap items-center justify-between gap-4">
          {role !== "district" ? (
            <div>
              <label className="block text-sm font-medium mb-1">Level</label>
              <div className="flex bg-slate-200/80 p-1 rounded-xl shrink-0 items-center h-[38px]">
                <button
                  type="button"
                  onClick={() => setEventLevelFilter("all")}
                  className={`px-4 py-1.5 rounded-lg text-xs font-bold transition-all ${eventLevelFilter === "all" ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"}`}
                >
                  All Levels
                </button>
                <button
                  type="button"
                  onClick={() => setEventLevelFilter("state")}
                  className={`px-4 py-1.5 rounded-lg text-xs font-bold transition-all ${eventLevelFilter === "state" ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"}`}
                >
                  State & Above
                </button>
                <button
                  type="button"
                  onClick={() => setEventLevelFilter("district")}
                  className={`px-4 py-1.5 rounded-lg text-xs font-bold transition-all ${eventLevelFilter === "district" ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"}`}
                >
                  District Level
                </button>
              </div>
            </div>
          ) : <div />}

          <div className="flex items-center gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Year</label>
              <SearchableSelect
                value={eventYearFilter}
                onChange={(e) => setEventYearFilter(e.target.value)}
                className="border p-2 rounded w-28 h-[38px] text-sm bg-white text-slate-800"
              >
                <option value="all">All Years</option>
                <option value="2026">2026</option>
                <option value="2025">2025</option>
                <option value="2024">2024</option>
              </SearchableSelect>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Status</label>
              <SearchableSelect
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="border p-2 rounded w-36 h-[38px] text-sm bg-white text-slate-800"
              >
                <option value="All">All</option>
                <option value="Pending">Pending</option>
                <option value="Approved">Approved</option>
                <option value="Rejected">Rejected</option>
              </SearchableSelect>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {(Array.isArray(filteredStats) ? filteredStats : []).map((s) => (
            <div key={s.event_id} className="p-4 border rounded-xl bg-white shadow-sm">
              <h3 
                onClick={() => setSelectedEvent(s.event_id)} 
                className="font-semibold text-blue-700 mb-2 cursor-pointer hover:underline"
              >
                {s.event_title}
              </h3>
              <p 
                onClick={() => { setSelectedEvent(s.event_id); setStatusFilter("Pending"); }} 
                className="cursor-pointer hover:text-amber-600 transition-all"
              >
                Pending: {s.pending || 0}
              </p>
              <p 
                onClick={() => { setSelectedEvent(s.event_id); setStatusFilter("Approved"); }} 
                className="text-green-700 cursor-pointer hover:text-green-800 transition-all"
              >
                Approved: {s.approved || 0}
              </p>
              <p 
                onClick={() => { setSelectedEvent(s.event_id); setStatusFilter("Rejected"); }} 
                className="text-red-600 cursor-pointer hover:text-red-700 transition-all"
              >
                Rejected: {s.rejected || 0}
              </p>
            </div>
          ))}
        </div>

        <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow mb-4 flex flex-wrap gap-4 items-end">
          <div>
            <label className="block text-sm font-medium mb-1">Event</label>
            <SearchableSelect
              value={selectedEvent}
              onChange={(e) => setSelectedEvent(e.target.value)}
              className="border p-2 rounded w-64"
            >
              <option value="">All Events</option>
              {(Array.isArray(filteredEventsForDropdown) ? filteredEventsForDropdown : []).map((ev) => (
                <option key={ev.id} value={ev.id}>
                  {ev.title}
                </option>
              ))}
            </SearchableSelect>
          </div>

          {eventLevelFilter === "district" && role !== "district" && (
            <div>
              <label className="block text-sm font-medium mb-1">District</label>
              <SearchableSelect
                value={selectedDistrictFilter}
                onChange={(e) => setSelectedDistrictFilter(e.target.value)}
                className="border p-2 rounded w-48 bg-white text-slate-800"
              >
                <option value="all">All Districts</option>
                {(Array.isArray(districtOptions) ? districtOptions : []).map((dist) => (
                  <option key={dist.id} value={dist.id}>
                    {dist.name}
                  </option>
                ))}
              </SearchableSelect>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium mb-1">Show</label>
            <SearchableSelect
              value={limit}
              onChange={(e) => setLimit(Number(e.target.value))}
              className="border p-2 rounded w-24"
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </SearchableSelect>
          </div>
        </div>

        <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4 mb-4 flex flex-wrap items-center justify-between gap-3 shadow-sm">
          <div className="flex items-center justify-between gap-3 mb-1 w-full">
            <span className="text-sm font-semibold text-slate-800">
              Bulk Actions {selectedIds.length > 0 ? `(${selectedIds.length} selected)` : "(Select checkboxes in the table to use)"}
            </span>
            {selectedIds.length > 0 && (
              <button
                type="button"
                onClick={() => setSelectedIds([])}
                className="text-xs text-red-600 hover:underline font-semibold"
              >
                Clear Selection
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleBulkToggleStatus}
              className="bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded text-xs font-semibold"
            >
              Approve/Reject
            </button>
          </div>
        </div>

        <section className="bg-white rounded-2xl border border-slate-200 shadow overflow-hidden">
          <div className="border-b border-slate-200 bg-slate-50/70 px-4 py-2.5">
            <h2 className="text-lg font-semibold text-slate-900">Volunteer Queue</h2>
            <p className="text-sm text-slate-600">Update status and handle certificate downloads for approved records.</p>
          </div>
        <div className="overflow-x-auto">
          {loading ? (
            <div className="p-6 text-center text-slate-500">Loading volunteer registrations...</div>
          ) : filteredRows.length === 0 ? (
            <div className="p-6 text-center text-slate-500">No volunteer registrations found.</div>
          ) : (
            <table className="min-w-[1200px] w-full text-xs sm:text-sm">
              <thead className="bg-slate-100 text-slate-700 select-none">
                <tr>
                  <th className="text-center px-3 py-2 w-10">
                    <input
                      type="checkbox"
                      checked={isAllSelected}
                      onChange={toggleSelectAll}
                      className="cursor-pointer rounded"
                    />
                  </th>
                  <th className="text-[11px] font-semibold px-2 py-2 text-center border border-gray-200">S.No.</th>
                  {[
                    { label: "Submitted", key: "created_at" },
                    { label: "Event", key: "event_title" },
                    { label: "Name", key: "name" },
                    { label: "Father Name", key: "father_name" },
                    { label: "Age", key: "age" },
                    { label: "Organisation", key: "organisation" },
                    { label: "Mobile", key: "mobile" },
                    { label: "District", key: "district" },
                    { label: "Role", key: "role" },
                    { label: "Status", key: "status" },
                  ].map((col, idx) => (
                    <th
                      key={idx}
                      onClick={() => handleSort(col.key)}
                      className="text-left px-3 py-2 cursor-pointer hover:bg-slate-200 transition"
                    >
                      <div className="flex items-center gap-1">
                        <span>{col.label}</span>
                        <span className="text-[10px] text-slate-400">
                          {sortConfig.key === col.key
                            ? sortConfig.direction === "asc"
                              ? "▲"
                              : "▼"
                            : "↕"}
                        </span>
                      </div>
                    </th>
                  ))}
                  <th className="text-left px-3 py-2">Certificate</th>
                </tr>
              </thead>
              <tbody>
                {paginatedRows.map((row, idx) => (
                  <tr key={row.id} className="border-t align-middle">
                    <td className="text-center px-3 py-2">
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(row.id)}
                        onChange={() => toggleSelectRow(row.id)}
                        className="cursor-pointer rounded"
                      />
                    </td>
                    <td className="text-center px-2 py-2 border border-gray-200 font-semibold">{(page - 1) * limit + idx + 1}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{formatDate(row.created_at)}</td>
                    <td className="px-3 py-2">
                      <div className="font-medium">{row.event_title || "-"}</div>
                      <div className="text-xs text-slate-500">
                        {formatDate(row.event_date)} {row.event_location ? `| ${row.event_location}` : ""}
                      </div>
                    </td>
                    <td className="px-3 py-2">{row.name || "-"}</td>
                    <td className="px-3 py-2">{row.father_name || "-"}</td>
                    <td className="px-3 py-2">{row.age || "-"}</td>
                    <td className="px-3 py-2">{row.organisation || "-"}</td>
                    <td className="px-3 py-2">{row.mobile || "-"}</td>
                    <td className="px-3 py-2">{row.district || "-"}</td>
                    <td className="px-3 py-2">
                      <span className={`px-2 py-0.5 rounded text-[11px] font-bold ${String(row.role || '').toLowerCase() === 'escort' ? 'bg-purple-100 text-purple-700' : 'bg-teal-100 text-teal-700'}`}>
                        {row.role || "Volunteer"}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      {(() => {
                        const status = String(row.status || "Pending");
                        const color =
                          status === "Approved"
                            ? "bg-green-100 text-green-700"
                            : status === "Rejected"
                              ? "bg-red-100 text-red-700"
                              : "bg-yellow-100 text-yellow-700";
                        return (
                          <div className="flex flex-col items-center gap-1">
                            <span className={`px-2 py-0.5 rounded text-[11px] font-semibold ${color}`}>
                              {status}
                            </span>
                            {row.district && (
                              <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-slate-600 bg-slate-100 border border-slate-200 px-1.5 py-0.5 rounded-full">
                                <span className="w-1.5 h-1.5 rounded-full bg-slate-400"></span>
                                {row.district}
                              </span>
                            )}
                          </div>
                        );
                      })()}
                    </td>
                    <td className="px-3 py-2">
                      {row.status === "Approved" ? (
                        <div>
                          <div className="flex items-center gap-1.5 flex-nowrap overflow-x-auto whitespace-nowrap pb-0.5">
                            <button
                              onClick={() => downloadCertificate(row, false)}
                              className="px-2.5 py-1 text-xs rounded bg-emerald-600 text-white hover:bg-emerald-700"
                            >
                              Download
                            </button>
                            <button
                              onClick={() => downloadCertificate(row, true)}
                              className="px-2.5 py-1 text-xs rounded bg-indigo-600 text-white hover:bg-indigo-700"
                            >
                              Regenerate
                            </button>
                          </div>
                          <div className="mt-1 text-[11px] text-slate-500">
                            {row.certificate_no ? `No: ${row.certificate_no}` : "Not issued yet"}
                          </div>
                        </div>
                      ) : (
                        <span className="text-[11px] text-slate-500">Approve to issue certificate</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        {totalPages > 1 && (
          <div className="relative z-10 flex justify-start sm:justify-center items-center gap-2 p-4 border-t bg-slate-50/50 overflow-x-auto whitespace-nowrap [scrollbar-width:thin]">
            <button
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
              className="px-3 py-1.5 rounded text-xs font-semibold border bg-white hover:bg-slate-50 disabled:opacity-50"
            >
              ◀ Prev
            </button>
            {getPageNumbers(page, totalPages).map((pNum, idx) => {
              if (pNum === "...") {
                return (
                  <span key={`ell-${idx}`} className="px-2 text-slate-500 font-medium select-none">
                    ...
                  </span>
                );
              }
              return (
                <button
                  key={pNum}
                  onClick={() => setPage(pNum)}
                  className={`px-3 py-1.5 rounded text-xs font-semibold border ${
                    page === pNum
                      ? "bg-emerald-600 text-white border-emerald-600 hover:bg-emerald-700"
                      : "bg-white text-slate-700 hover:bg-slate-50"
                  }`}
                >
                  {pNum}
                </button>
              );
            })}
            <button
              disabled={page === totalPages}
              onClick={() => setPage(page + 1)}
              className="px-3 py-1.5 rounded text-xs font-semibold border bg-white hover:bg-slate-50 disabled:opacity-50"
            >
              Next ▶
            </button>
          </div>
        )}
        </section>
      </div>
    </>
  );
};

export default VolunteerRegistrations;
